import type { Category, CategoryAlias } from "@/types/directory";

/**
 * Category matching helpers.
 *
 * The A–Z index lists hundreds of keyword-level categories, while only a small
 * set of curated "branded" categories exist (the ones with their own page at
 * /category/<slug>, with an icon, description and sub-categories). These
 * helpers decide whether an A–Z name corresponds to one of those branded
 * categories so it can link straight to the branded page instead of a keyword
 * search.
 */

/** Lowercase, alphanumeric-only slug — mirrors the backend's category slug rules. */
export function slugifyCategory(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Word tokens with punctuation and ampersands stripped, so "Schools",
 * "Schools & Coaching" and "Hospitals" can be compared fairly. A light plural
 * trim keeps "hotels"/"hotel" and "restaurants"/"restaurant" aligned.
 */
function tokenize(value: string): string[] {
  return value
    .toLowerCase()
    .replace(/&/g, " ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((token) => (token.length > 3 && token.endsWith("s") ? token.slice(0, -1) : token));
}

/** True when every token of `a` also appears in `b` (b covers a). */
function isTokenSubset(a: string[], b: string[]): boolean {
  if (a.length === 0) return false;
  const setB = new Set(b);
  return a.every((token) => setB.has(token));
}

/** Normalise an A–Z name into the key used for explicit alias lookups. */
function aliasKey(value: string): string {
  return value.trim().toLowerCase();
}

/**
 * Build a lookup of A–Z name → branded slug from the admin-managed aliases.
 * Keys are normalised (trimmed + lowercased) so matching is case-insensitive.
 */
export function buildAliasMap(aliases: CategoryAlias[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const alias of aliases) {
    if (!alias.azName || !alias.categorySlug) continue;
    map.set(aliasKey(alias.azName), alias.categorySlug);
  }
  return map;
}

/**
 * Resolve the branded category slug for an A–Z category name, or null when the
 * name has no branded page and should stay a keyword search.
 *
 * Resolution order:
 *   0. explicit admin alias (if provided and its target still exists)
 *   1. exact slug or exact normalised name (e.g. "Information Technology")
 *   2. token subset — the A–Z name is fully contained in a branded name
 *      (e.g. "Schools" → "Schools & Coaching", "Hospitals" → "Hospitals & Clinics")
 *
 * An alias is ignored when its target slug no longer exists among the branded
 * categories, so a deleted category can never produce a dead link.
 */
export function findBrandedCategorySlug(
  azName: string,
  branded: Pick<Category, "slug" | "name">[],
  aliases?: Map<string, string>,
): string | null {
  if (!azName || branded.length === 0) return null;

  const slugSet = new Set(branded.map((cat) => cat.slug));

  // Pass 0 — explicit admin alias (wins over everything).
  if (aliases) {
    const aliased = aliases.get(aliasKey(azName));
    if (aliased && slugSet.has(aliased)) return aliased;
  }

  const azSlug = slugifyCategory(azName);
  const azTokens = tokenize(azName);
  const azKey = azTokens.join(" ");

  // Pass 1 — exact slug / exact name.
  for (const cat of branded) {
    if (cat.slug === azSlug) return cat.slug;
    if (tokenize(cat.name).join(" ") === azKey) return cat.slug;
  }

  // Pass 2 — the A–Z name is a token subset of a branded name.
  for (const cat of branded) {
    if (isTokenSubset(azTokens, tokenize(cat.name))) return cat.slug;
  }

  return null;
}

/**
 * Locally-flavoured intro copy for a category page. Uses the curated
 * description when it is substantial enough, otherwise composes one so the page
 * always has a real, SEO-worthy heading description.
 */
export function buildCategoryDescription(name: string, custom?: string): string {
  const trimmed = (custom ?? "").trim();
  if (trimmed.length >= 60) return trimmed;
  return `Browse verified ${name} businesses across Mangaluru, Udupi and coastal Karnataka — addresses, ratings, phone numbers, opening hours and customer reviews, all in one place.`;
}